import type { Feature, Polygon, Position } from "geojson";
import { polygonOverlaps } from "./validate";
import { unionPolygons } from "./union";
import { isPolygonValid } from "./validate";
import type { GeometryResult } from "../types";

/** MultiPolygon coordinates: array of polygon parts, each with rings */
export type MultiPolygonCoords = Position[][][];

/**
 * Merge two overlapping polygons into one.
 * Returns the merged polygon's coordinates in MultiPolygon format.
 */
export function mergePolygons(
	a: Feature<Polygon>,
	b: Feature<Polygon>,
): GeometryResult<{ coordinates: MultiPolygonCoords }> {
	if (!polygonOverlaps(a, b).overlaps) {
		return { ok: false, error: "Polygons do not intersect, aborting." };
	}

	const unionResult = unionPolygons([a, b]);
	if (!unionResult.ok) {
		return { ok: false, error: unionResult.error };
	}

	const merged = unionResult.data;
	const { isValid } = isPolygonValid(merged);
	if (!isValid) {
		return { ok: false, error: "Merged polygon is not valid, aborting." };
	}

	if (merged.geometry.type === "MultiPolygon") {
		return { ok: true, data: { coordinates: merged.geometry.coordinates } };
	}

	if (merged.geometry.type === "Polygon") {
		return { ok: true, data: { coordinates: [merged.geometry.coordinates] } };
	}

	return { ok: false, error: "Unexpected geometry type after merge." };
}

/**
 * Merge two overlapping holes into one.
 * Returns the outer ring of the merged hole.
 */
export function mergeHoles(a: Feature<Polygon>, b: Feature<Polygon>): GeometryResult<Position[]> {
	if (!polygonOverlaps(a, b).overlaps) {
		return { ok: false, error: "Holes do not intersect, aborting." };
	}

	const unionResult = unionPolygons([a, b]);
	if (!unionResult.ok) {
		return { ok: false, error: unionResult.error };
	}

	const merged = unionResult.data;
	const { isValid } = isPolygonValid(merged);
	if (!isValid) {
		return { ok: false, error: "Merged hole is not valid, aborting." };
	}

	if (merged.geometry.type === "MultiPolygon") {
		return { ok: true, data: merged.geometry.coordinates[0][0] };
	}

	if (merged.geometry.type === "Polygon") {
		return { ok: true, data: merged.geometry.coordinates[0] };
	}

	return { ok: false, error: "Unexpected geometry type after merge." };
}
